import { z } from 'zod';
import { idSchema, isoDateTimeSchema, paginationQuerySchema } from './primitives';
import { accessScopeSchema, permissionSchema, roleSchema } from './rbac';
/**
 * Identités et authentification (cahier des charges §8, §16).
 *
 * Le mot de passe n'apparaît jamais dans un schéma de sortie : les DTO de
 * lecture et d'écriture sont volontairement disjoints pour rendre une fuite
 * accidentelle impossible par construction.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Le mot de passe doit comporter au moins ${PASSWORD_MIN_LENGTH} caractères`)
    .max(128, 'Mot de passe trop long')
    .refine((value) => /[a-z]/.test(value), 'Au moins une minuscule est requise')
    .refine((value) => /[A-Z]/.test(value), 'Au moins une majuscule est requise')
    .refine((value) => /[0-9]/.test(value), 'Au moins un chiffre est requis');
export const userSchema = z.object({
    id: idSchema,
    email: z.string().email(),
    fullName: z.string(),
    role: roleSchema,
    branchId: idSchema,
    branchCode: z.string(),
    branchName: z.string(),
    /** Bureaux supplémentaires supervisés — renseigné pour les Area Managers. */
    managedBranchIds: z.array(idSchema),
    additionalPermissions: z.array(permissionSchema),
    isActive: z.boolean(),
    mustChangePassword: z.boolean(),
    lastLoginAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/** Identité complète du porteur du jeton, telle que consommée par le front. */
export const currentUserSchema = userSchema.extend({
    permissions: z.array(permissionSchema),
    scope: accessScopeSchema,
    /** Bureaux effectivement accessibles ; `null` signifie « tous les bureaux ». */
    accessibleBranchIds: z.array(idSchema).nullable(),
});
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Adresse e-mail invalide'),
    password: z.string().min(1, 'Mot de passe requis'),
    rememberMe: z.boolean().default(false),
});
export const authTokensSchema = z.object({
    accessToken: z.string(),
    /** Durée de vie du jeton d'accès, en secondes. */
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
});
export const loginResponseSchema = authTokensSchema.extend({
    user: currentUserSchema,
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Mot de passe actuel requis'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'La confirmation ne correspond pas',
    path: ['confirmPassword'],
})
    .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'Le nouveau mot de passe doit différer de l’actuel',
    path: ['newPassword'],
});
export const createUserSchema = z.object({
    email: z.string().trim().toLowerCase().email('Adresse e-mail invalide'),
    fullName: z.string().trim().min(2, 'Nom complet requis').max(160),
    role: roleSchema,
    branchId: idSchema,
    managedBranchIds: z.array(idSchema).default([]),
    additionalPermissions: z.array(permissionSchema).default([]),
    temporaryPassword: passwordSchema,
});
export const updateUserSchema = z.object({
    fullName: z.string().trim().min(2).max(160).optional(),
    role: roleSchema.optional(),
    branchId: idSchema.optional(),
    managedBranchIds: z.array(idSchema).optional(),
    additionalPermissions: z.array(permissionSchema).optional(),
    isActive: z.boolean().optional(),
});
export const resetUserPasswordSchema = z.object({
    temporaryPassword: passwordSchema,
});
export const listUsersQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().max(160).optional(),
    role: roleSchema.optional(),
    branchId: idSchema.optional(),
    isActive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true'))
        .optional(),
});
//# sourceMappingURL=identity.js.map